import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  useBuilders,
  useCreateBuilder,
  useDeleteBuilder,
  downloadBuilderExport,
  type CreateBuilderInput,
} from '../api/builders';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { Button } from '../components/ui/Button';
import { DataTable } from '../components/ui/DataTable';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../hooks/useToast';
import { useAuthState } from '../auth/AuthProvider';
import page from './Page.module.css';
import styles from './BuildersPage.module.css';

type BuilderRow = {
  id: number;
  name: string;
  community_count?: number;
  mto_count?: number;
  model_count?: number;
  models_missing_mto_count?: number;
  total_lines?: number;
};

function IconExport() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPillCommunities() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v0M9 12v0M9 15v0M9 18v0" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPillMtos() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPillLines() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPillMissingMto() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 9v4M12 17h.01" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StatPills({ row }: { row: BuilderRow }) {
  const c = Number(row.community_count ?? 0);
  const m = Number(row.mto_count ?? 0);
  const l = Number(row.total_lines ?? 0);
  const modelCount = Number(row.model_count ?? 0);
  const missingMto = Number(row.models_missing_mto_count ?? 0);
  return (
    <div className={styles.pillRow}>
      <span className={`${styles.pill} ${styles.pillCommunities}`} title={`${c} communities`}>
        <IconPillCommunities />
        {c}
      </span>
      <span className={`${styles.pill} ${styles.pillMtos}`} title={`${m} MTOs`}>
        <IconPillMtos />
        {m}
      </span>
      <span className={`${styles.pill} ${styles.pillLines}`} title={`${l} lines`}>
        <IconPillLines />
        {l}
      </span>
      {modelCount > 0 ? (
        <span
          className={`${styles.pill} ${missingMto > 0 ? styles.pillMissingMto : styles.pillMissingMtoOk}`}
          title={
            missingMto > 0
              ? `${missingMto} of ${modelCount} catalog models missing MTO data`
              : `All ${modelCount} catalog models have MTO data`
          }
        >
          <IconPillMissingMto />
          {missingMto} Missing MTO{missingMto === 1 ? '' : 's'}
        </span>
      ) : null}
    </div>
  );
}

export function BuildersPage() {
  const toast = useToast();
  const auth = useAuthState();
  const { data, isLoading, error } = useBuilders();
  const create = useCreateBuilder();
  const del = useDeleteBuilder();

  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [sageCustomerId, setSageCustomerId] = useState('');
  const [notes, setNotes] = useState('');
  const [exportingId, setExportingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BuilderRow | null>(null);

  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'admin';

  function resetModal() {
    setName('');
    setSageCustomerId('');
    setNotes('');
  }

  function openModal() {
    resetModal();
    setModalOpen(true);
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const payload: CreateBuilderInput = {
      name: name.trim(),
      sage_customer_id: sageCustomerId.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      await create.mutateAsync(payload);
      toast(`Created builder "${payload.name}"`);
      setModalOpen(false);
      resetModal();
    } catch (ex) {
      toast(ex instanceof Error ? ex.message : 'Create failed', true);
    }
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    del.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast('Builder deleted');
        setDeleteTarget(null);
      },
      onError: (ex) => toast(ex instanceof Error ? ex.message : 'Delete failed', true),
    });
  }

  const rows: BuilderRow[] = useMemo(() => (data ?? []) as BuilderRow[], [data]);

  const columns = useMemo<ColumnDef<BuilderRow, unknown>[]>(
    () => [
      {
        id: 'actions',
        header: '',
        meta: { align: 'left' as const },
        size: 100,
        cell: ({ row }) => {
          const b = row.original;
          return (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.iconBtn}
                title="Export Excel"
                aria-label={`Export ${b.name} to Excel`}
                disabled={exportingId === b.id}
                onClick={() => {
                  setExportingId(b.id);
                  downloadBuilderExport(b.id)
                    .catch((ex) => toast(ex instanceof Error ? ex.message : 'Export failed', true))
                    .finally(() => setExportingId(null));
                }}
              >
                <IconExport />
              </button>
              {isAdmin ? (
                <button
                  type="button"
                  className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                  title="Delete Builder"
                  aria-label={`Delete ${b.name}`}
                  onClick={() => setDeleteTarget(b)}
                >
                  <IconTrash />
                </button>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'name',
        header: 'Builder',
        cell: ({ row }) => (
          <div className={styles.nameCell}>
            <Link className={styles.nameLink} to={`/builders/${row.original.id}`}>
              {row.original.name}
            </Link>
          </div>
        ),
      },
      {
        id: 'stats',
        header: '',
        meta: { align: 'right' as const },
        cell: ({ row }) => <StatPills row={row.original} />,
      },
    ],
    [exportingId, isAdmin, toast]
  );

  return (
    <div>
      <Breadcrumb items={[{ label: 'Builders' }]} />

      <div className={styles.toolbar}>
        <Button type="button" variant="success" onClick={openModal}>
          Add New Builder
        </Button>
      </div>

      <div className={page.card}>
        <h2 className={page.title} style={{ marginBottom: 16 }}>
          Builders
        </h2>
        {isLoading ? <p className={page.muted}>Loading…</p> : null}
        {error ? <p style={{ color: 'var(--color-danger)' }}>{String(error)}</p> : null}
        {!isLoading && !error && rows.length === 0 ? (
          <p className={page.muted}>No builders yet. Use &quot;Add New Builder&quot; to create one.</p>
        ) : null}
        {!isLoading && rows.length > 0 ? <DataTable data={rows} columns={columns} /> : null}
      </div>

      <Modal
        title="Add New Builder"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        size="wide"
        showClose={false}
      >
        <form onSubmit={onCreate} className={styles.modalForm}>
          <Input label="Builder Name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          <Input
            label="Sage Customer ID"
            value={sageCustomerId}
            onChange={(e) => setSageCustomerId(e.target.value)}
            placeholder="Optional"
          />
          <label className={page.muted} style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Notes
          </label>
          <textarea
            className={styles.textarea}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
            rows={4}
          />
          <div className={styles.modalActions}>
            <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="success" disabled={create.isPending || !name.trim()}>
              {create.isPending ? 'Creating…' : 'Create Builder'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        title="Delete Builder?"
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        showClose={false}
      >
        {deleteTarget ? (
          <>
            <div className={styles.deleteBody}>
              <p className={styles.deleteLead}>Delete &quot;{deleteTarget.name}&quot;?</p>
              <p className={styles.deleteWarn}>
                This removes the builder and all related communities, MTOs, lines, and bid data. This cannot be undone.
              </p>
            </div>
            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={() => setDeleteTarget(null)} disabled={del.isPending}>
                Cancel
              </Button>
              <Button type="button" variant="danger" onClick={confirmDelete} disabled={del.isPending}>
                {del.isPending ? 'Deleting…' : 'Delete Builder'}
              </Button>
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
}
